<script setup>
import { useDataStore } from '@/stores/data'
import { useAppStore } from '@/stores/app'
import { ref } from 'vue'
import { ElDatePicker, ElConfigProvider } from 'element-plus' // Import the DatePicker component
import 'element-plus/es/components/date-picker/style/css' // Import DatePicker styles
import no from 'element-plus/dist/locale/nb-no.mjs'

const appStore = useAppStore()
const dataStore = useDataStore()

defineProps({
	title: {
		type: String,
		default: '',
	},
	hasDatePicker: {
		type: Boolean,
		default: false,
	},
	hasRefreshButton: {
		type: Boolean,
		default: false,
	},
	hasTripsNavigation: {
		type: Boolean,
		default: false,
	},
})

const datePickerRef = ref(null)

const selectedDate = ref(dataStore.selectedDate)

const formatDate = (date) => {
	if (!isValidDate(date)) return ''
	const formatter = new Intl.DateTimeFormat('nb-NO', {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
	})
	let formattedDate = formatter.format(date)
	return formattedDate.replace(/(\d+)/, '$1')
}

const showCalendar = () => {
	console.warn('availableDates', dataStore.availableDates, datePickerRef.value)
	datePickerRef.value.focus() // Opens the DatePicker popup
	modifyDatePickerFooter()
}

const disabledDate = (date) => {
	var dateShort = date.toShortDateString()
	return !dataStore.availableDates[dateShort]
}

const cellClassName = (date) => {
	var status = 'open'
	var dateShort = date.toShortDateString()
	var savedDay = dataStore.savedDaysLookup[dateShort]
	if (savedDay) {
		if (savedDay.approved) {
			status = 'approved'
		} else {
			// Check if any of the trips are approved
			for (var journey of savedDay.journeys) {
				if (journey.approved || journey.verified) {
					status = 'pending'
					break
				}
			}
		}
	}
	if (dataStore.availableDates[dateShort]) {
		return 'has-data ' + status
	} else {
		return 'no-data ' + status
	}
}

const isValidDate = (date) => {
	return date instanceof Date && !isNaN(date.getTime())
}

const handleDateChange = (date) => {
	console.warn('handleDateChange', date)
	dataStore.currentJourneyIndex = 0
	dataStore.selectedDate = date
	dataStore.processing = true
	appStore.loaded = false
	dataStore.loadAndProcessData(true)
}

const modifyDatePickerFooter = () => {
	// Find all date pickers
	const pickers = document.querySelectorAll('.el-date-picker')
	if (!pickers.length) {
		console.warn('No date pickers found')
		return
	}

	// Add footer to each picker
	pickers.forEach((picker) => {
		// Check if we already added a footer
		const existingFooter = picker.querySelector('.custom-footer')
		if (existingFooter) return

		// Create and append the footer
		const legendElement = document.createElement('div')
		legendElement.className = 'custom-footer'
		legendElement.innerHTML = `
        <div class="legend">
          <div><span class="dot available"></span> Åpen</div>
			 <div><span class="dot approved"></span> Godkjent</div>
			 <div><span class="dot pending"></span> Påbegynt</div>
        </div>
      `
		picker.appendChild(legendElement)
	})
}

const nextJourney = (direction) => {
	dataStore.nextJourney(direction)
	dataStore.evaluateCurrentJourneySurveyResponses()
	setTimeout(() => {
		if (appStore.editStep == '') {
			appStore.bottomSheet.setHeightNormal(300, 'DefaultHeader nextJourney', false)
		}
	}, 200)
}
</script>

<template>
	<div class="label flex" v-if="hasTripsNavigation && dataStore.currentJourneys.length > 0">
		<div class="button transparent prev" @click="nextJourney(-1)" v-if="dataStore.userCanNavigateTrips && dataStore.currentJourneyIndex > 0 && (appStore.editStep == '' || appStore.editStep == 'change')">
			<i class="fa-solid fa-chevron-left"></i>
		</div>
		<div>
			<template v-if="appStore.editStep == 'joinNextJourney' || appStore.editStep == 'joinPrevJourney'">Slå sammen reiser </template>
			<template v-if="appStore.editStep == 'addTrip'">Legg til ny etappe </template>
			<template v-if="appStore.editStep == 'deleteTrip'">Slett en etappe på reisen</template>
			<template v-if="appStore.editStep == 'joinTrips'">Slå sammen valgte etapper </template>
			<template v-if="appStore.editStep == 'splitTrip'">Del opp etappe til to etapper</template>
			<template v-if="appStore.editStep == 'splitJourney'">Dele opp til to reiser med ulikt formål</template>
			<template v-if="appStore.editStep == 'surveyTrip'">Spesifisering av etapper</template>
			<template v-if="appStore.editStep == 'change'"> Endre reise {{ dataStore.currentJourneyIndex + 1 }} av {{ dataStore.currentJourneys.length }}</template>
			<template v-if="appStore.editStep == ''">Reise {{ dataStore.currentJourneyIndex + 1 }} av {{ dataStore.currentJourneys.length }}</template>
		</div>
		<div class="button transparent next" @click="nextJourney(1)" v-if="dataStore.userCanNavigateTrips && dataStore.currentJourneyIndex < dataStore.currentJourneys.length - 1 && (appStore.editStep == '' || appStore.editStep == 'change')">
			<i class="fa-solid fa-chevron-right"></i>
		</div>
	</div>
	<div v-else>{{ title }}</div>
	<div v-if="hasRefreshButton && !hasDatePicker" class="absolute-buttons">
		<div class="button white smaller border" @click.prevent="appStore.startInit(true)"><i class="fa-solid fa-arrows-rotate"></i>Oppdater</div>
	</div>
	<div v-if="hasDatePicker && dataStore.hasAvailableDates && appStore.editStep == ''" class="absolute-buttons">
		<template v-if="dataStore.userCanChangeDates">
			<div class="button active date" @click.prevent="showCalendar">
				<i class="fa-regular fa-calendar"></i>&nbsp;
				{{ formatDate(dataStore.selectedDate) }}
			</div>
			<el-config-provider :locale="no">
				<el-date-picker popper-class="datepicker-popper" @change="handleDateChange" :clearable="true" :cell-class-name="cellClassName" :disabledDate="disabledDate" :editable="false" trigger="manual" placement="right" class="datePickerInput" v-model="selectedDate" ref="datePickerRef" placeholder="Pick a date" type="date">
					<template #prev-year>
						<div></div>
					</template>
					<template #next-year>
						<div></div>
					</template>
				</el-date-picker>
			</el-config-provider>
		</template>
		<template v-else>
			<div class="button date transparent">
				{{ formatDate(dataStore.selectedDate) }}
			</div>
		</template>
	</div>
</template>

<style>
.custom-footer {
	background: white;
	border-top: 1px solid #eee;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
}

.legend {
	display: flex;
	width: 100%;
	padding: 1.25rem;
	padding-top: 0.25rem;
	padding-bottom: 0.25rem;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
}

.dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	display: inline-block;
	margin-right: 5px;
}

.dot.available {
	background-color: #ddd;
}

.dot.approved {
	background-color: var(--color-primary);
}

.dot.pending {
	background-color: var(--color-danger);
}

.el-picker-panel__footer button {
	display: none;
}

.el-picker-panel__footer.visible {
	max-height: 40px;
}

.el-date-table-cell__text {
	--color: var(--el-text-color-regular);
}

.disabled .el-date-table-cell {
	background-color: transparent !important;
	color: #ccc !important;
	cursor: not-allowed;
	opacity: 1;
}

.available .el-date-table-cell__text,
.has-data .el-date-table-cell__text {
	background-color: #eee;
	font-weight: 500;
}
.available .el-date-table-cell__text:hover {
	color: var(--el-text-color-regular);
}

.approved .el-date-table-cell__text {
	--color: var(--color-primary);
}

.approved .el-date-table-cell__text:after {
	font-family: 'Font Awesome 6 Free';
	font-weight: 900;
	content: '\f00c';
	display: block;
	width: 100%;
	height: 100%;
	background-color: var(--color-primary);
	position: absolute;
	border-radius: 100%;
	color: white;
	top: 0px;
	left: 0px;
}
.approved.current .el-date-table-cell__text:after {
	display: none;
}

.pending .el-date-table-cell__text {
	--color: var(--color-danger);
	background-color: var(--color-danger) !important;
	color: white;
}

.pending.disabled .el-date-table-cell__text,
.approved.disabled .el-date-table-cell__text {
	opacity: 0.3;
	font-weight: 600;
	font-size: 1rem;
}

.el-date-table .current .el-date-table-cell__text {
	line-height: 20px !important;

	padding: 2px;
}

.current .el-date-table-cell__text {
	background-color: white !important;
	color: var(--color) !important;
	outline: 2px var(--color) solid; /* inner stroke */
	outline-offset: 2px;
	box-sizing: border-box;
}

.el-date-table td.today .el-date-table-cell__text {
	color: black;
}

.el-month-table .el-date-table-cell__text {
	background-color: #eee;
}

.el-month-table .disabled .el-date-table-cell__text,
.el-year-table .disabled .el-date-table-cell__text {
	opacity: 0.5;
}

.prev-month {
	opacity: 0;
	pointer-events: none;
}
.next-month {
	display: none;
}
</style>
